import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  TerminalStatusFilter,
  TerminalStatusListItem,
  TerminalStatusSummary,
} from '../../../models/terminal-status.model';
import { TerminalStatusService } from '../../../services/terminal-status.service';
import {
  terminalLastSeenAge,
  terminalStatusClass,
  terminalStatusLabel,
} from '../../../shared/terminal-liveness';

/** A summary tile that is also the filter control for the thing it counts. */
interface StatusChip {
  key: TerminalStatusFilter;
  label: string;
  count: (s: TerminalStatusSummary) => number;
  tone: string;
}

@Component({
  selector: 'app-terminals-page',
  standalone: false,
  templateUrl: './terminals-page.component.html',
  styleUrls: ['./terminals-page.component.css'],
})
export class TerminalsPageComponent implements OnInit, OnDestroy {
  readonly pageSize = 25;

  terminals: TerminalStatusListItem[] = [];
  summary: TerminalStatusSummary | null = null;
  isLoading = false;
  error = '';

  page = 1;
  totalCount = 0;
  totalPages = 1;

  status: TerminalStatusFilter = '';
  searchTerm = '';

  /** How long a terminal may stay silent before the server calls it offline. */
  offlineAfterMinutes = 5;

  /**
   * The server's clock at the time of the last load. Ages are measured against it rather than the
   * browser's, so a row never reads "3 minutes ago" next to a status the server decided using a
   * different now.
   */
  private asOf: Date | null = null;

  readonly chips: StatusChip[] = [
    { key: '', label: 'All terminals', count: s => s.total, tone: 'chip-all' },
    { key: 'offline', label: 'Offline', count: s => s.offline, tone: 'chip-red' },
    { key: 'never', label: 'Never connected', count: s => s.neverConnected, tone: 'chip-grey' },
    { key: 'online', label: 'Online', count: s => s.online, tone: 'chip-green' },
    { key: 'offline-mode', label: 'In offline mode', count: s => s.offlineMode, tone: 'chip-amber' },
    { key: 'disabled', label: 'Disabled', count: s => s.disabled, tone: 'chip-grey' },
  ];

  private readonly searchSubject = new Subject<string>();
  private readonly subs = new Subscription();

  constructor(private svc: TerminalStatusService, private router: Router) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchSubject.pipe(debounceTime(350), distinctUntilChanged()).subscribe(query => {
        this.searchTerm = query;
        this.page = 1;
        this.load();
      })
    );

    this.load();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  load(): void {
    this.isLoading = true;
    this.error = '';

    this.svc.getTerminals(this.page, this.pageSize, this.status, this.searchTerm).subscribe({
      next: result => {
        this.terminals = result.results?.items ?? [];
        this.totalCount = result.results?.total ?? 0;
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
        this.summary = result.summary ?? null;
        this.offlineAfterMinutes = result.offlineAfterMinutes || this.offlineAfterMinutes;
        this.asOf = result.asOfUtc ? new Date(result.asOfUtc) : null;
      },
      error: () => {
        this.terminals = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.error = 'Failed to load terminals.';
      },
    }).add(() => { this.isLoading = false; });
  }

  onSearch(term: string): void {
    this.searchSubject.next((term || '').trim());
  }

  setStatus(status: TerminalStatusFilter): void {
    if (this.status === status) return;
    this.status = status;
    this.page = 1;
    this.load();
  }

  chipCount(chip: StatusChip): number {
    return this.summary ? chip.count(this.summary) : 0;
  }

  get rangeLabel(): string {
    if (!this.totalCount) return '0 terminals';
    const start = (this.page - 1) * this.pageSize + 1;
    const end = Math.min(this.page * this.pageSize, this.totalCount);
    return `${start}–${end} of ${this.totalCount}`;
  }

  setPage(page: number): void {
    const clamped = Math.max(1, Math.min(page, this.totalPages));
    if (clamped === this.page) return;
    this.page = clamped;
    this.load();
  }

  terminalLabel(t: TerminalStatusListItem): string {
    return t.terminalName?.trim() || `Terminal ${t.terminalNo}`;
  }

  statusLabel(t: TerminalStatusListItem): string {
    return terminalStatusLabel(t.status);
  }

  statusClass(t: TerminalStatusListItem): string {
    return terminalStatusClass(t.status);
  }

  lastSeenAge(t: TerminalStatusListItem): string {
    return terminalLastSeenAge(t.lastSeenUtc, this.asOf);
  }

  openBusiness(t: TerminalStatusListItem): void {
    if (!t.businessId) return;
    this.router.navigate(['/businesses', t.businessId]);
  }

  trackByTerminalId(_index: number, terminal: TerminalStatusListItem): string {
    return terminal.terminalId;
  }
}
